import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
    getCertificateDataApi,
    CertificateDataParams,
    CertificateDataResponse,
    CertificateMonthData,
    submitCertificateApi,
    SubmitCertificatePayload,
    SubmitCertificateResponse,
    getEmployeeCertificatesApi,
    getDraftEmployeeCertificatesApi,
    GetCertificatesParams,
    EmployeeCertificate,
    EmployeeCertificatesResponse,
    deleteCertificateApi,
    deleteDraftCertificateApi,
    getCertificateByIdApi,
    getDraftCertificateByIdApi,
    updateCertificateApi,
    updateDraftCertificateApi,
    CertificateByIdResponse,
    getEmployeeByIdApi,
    EmployeeData,
    saveDraftCertificateApi,
    SaveDraftPayload,
} from './employer-certificate.service';

export const getCertificateData = createAsyncThunk<
    CertificateDataResponse,
    CertificateDataParams,
    { rejectValue: string }
>(
    'employerCertificate/getCertificateData',
    async (params, { rejectWithValue }) => {
        try {
            const response = await getCertificateDataApi(params);
            return response;
        } catch (error: any) {
            const message =
                error?.response?.data?.message ||
                error?.message ||
                'Failed to fetch certificate data';
            return rejectWithValue(message);
        }
    }
);

export const submitCertificate = createAsyncThunk<
    SubmitCertificateResponse,
    SubmitCertificatePayload,
    { rejectValue: string }
>(
    'employerCertificate/submitCertificate',
    async (payload, { rejectWithValue }) => {
        try {
            return await submitCertificateApi(payload);
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || error?.message || 'Failed to submit certificate'
            );
        }
    }
);

export const saveDraftCertificate = createAsyncThunk<
    SubmitCertificateResponse,
    SaveDraftPayload,
    { rejectValue: string }
>(
    'employerCertificate/saveDraftCertificate',
    async (payload, { rejectWithValue }) => {
        try {
            return await saveDraftCertificateApi(payload);
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || error?.message || 'Failed to save draft'
            );
        }
    }
);

export const getEmployeeCertificates = createAsyncThunk<
    EmployeeCertificatesResponse,
    GetCertificatesParams | undefined,
    { rejectValue: string }
>(
    'employerCertificate/getEmployeeCertificates',
    async (params, { rejectWithValue }) => {
        try {
            return await getEmployeeCertificatesApi(params);
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || error?.message || 'Failed to fetch certificates'
            );
        }
    }
);

export const getDraftEmployeeCertificates = createAsyncThunk<
    EmployeeCertificatesResponse,
    GetCertificatesParams | undefined,
    { rejectValue: string }
>(
    'employerCertificate/getDraftEmployeeCertificates',
    async (params, { rejectWithValue }) => {
        try {
            return await getDraftEmployeeCertificatesApi(params);
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || error?.message || 'Failed to fetch draft certificates'
            );
        }
    }
);

export const getCertificateById = createAsyncThunk<
    CertificateByIdResponse,
    number,
    { rejectValue: string }
>(
    'employerCertificate/getCertificateById',
    async (certificateId, { rejectWithValue }) => {
        try {
            return await getCertificateByIdApi(certificateId);
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || error?.message || 'Failed to fetch certificate'
            );
        }
    }
);

export const getDraftCertificateById = createAsyncThunk<
    CertificateByIdResponse,
    number,
    { rejectValue: string }
>(
    'employerCertificate/getDraftCertificateById',
    async (certificateId, { rejectWithValue }) => {
        try {
            return await getDraftCertificateByIdApi(certificateId);
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || error?.message || 'Failed to fetch draft certificate'
            );
        }
    }
);

export const updateCertificate = createAsyncThunk<
    any,
    any,
    { rejectValue: string }
>(
    'employerCertificate/updateCertificate',
    async (payload, { rejectWithValue }) => {
        try {
            return await updateCertificateApi(payload);
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || error?.message || 'Failed to update certificate'
            );
        }
    }
);

export const updateDraftCertificate = createAsyncThunk<
    any,
    { certificateId: number; payload: any },
    { rejectValue: string }
>(
    'employerCertificate/updateDraftCertificate',
    async ({ certificateId, payload }, { rejectWithValue }) => {
        try {
            return await updateDraftCertificateApi(certificateId, payload);
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || error?.message || 'Failed to update draft certificate'
            );
        }
    }
);

export const deleteCertificate = createAsyncThunk<
    number,
    number,
    { rejectValue: string }
>(
    'employerCertificate/deleteCertificate',
    async (certificateId, { rejectWithValue }) => {
        try {
            await deleteCertificateApi(certificateId);
            return certificateId;
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || error?.message || 'Failed to delete certificate'
            );
        }
    }
);

export const deleteDraftCertificate = createAsyncThunk<
    number,
    number,
    { rejectValue: string }
>(
    'employerCertificate/deleteDraftCertificate',
    async (certificateId, { rejectWithValue }) => {
        try {
            await deleteDraftCertificateApi(certificateId);
            return certificateId;
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || error?.message || 'Failed to delete draft certificate'
            );
        }
    }
);

export const getEmployeeById = createAsyncThunk<
    EmployeeData,
    number,
    { rejectValue: string }
>(
    'employerCertificate/getEmployeeById',
    async (userId, { rejectWithValue }) => {
        try {
            const res = await getEmployeeByIdApi(userId);
            return res.response;
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || error?.message || 'Failed to fetch employee data'
            );
        }
    }
);

interface EmployerCertificateState {
    certificateData: CertificateMonthData[];
    loading: boolean;
    error: string | null;
    submitting: boolean;
    submitError: string | null;
    savingDraft: boolean;
    saveDraftError: string | null;
    employeeCertificates: EmployeeCertificate[];
    certificatesLoading: boolean;
    certificatesError: string | null;
    draftEmployeeCertificates: EmployeeCertificate[];
    draftCertificatesLoading: boolean;
    draftCertificatesError: string | null;
    deleting: boolean;
    deleteError: string | null;
    selectedCertificate: EmployeeCertificate | null;
    selectedCertificateLoading: boolean;
    selectedCertificateError: string | null;
    updating: boolean;
    updateError: string | null;
    updatingDraft: boolean;
    updateDraftError: string | null;
    employeeData: EmployeeData | null;
    employeeDataLoading: boolean;
    employeeDataError: string | null;
}

const initialState: EmployerCertificateState = {
    certificateData: [],
    loading: false,
    error: null,
    submitting: false,
    submitError: null,
    savingDraft: false,
    saveDraftError: null,
    employeeCertificates: [],
    certificatesLoading: false,
    certificatesError: null,
    draftEmployeeCertificates: [],
    draftCertificatesLoading: false,
    draftCertificatesError: null,
    deleting: false,
    deleteError: null,
    selectedCertificate: null,
    selectedCertificateLoading: false,
    selectedCertificateError: null,
    updating: false,
    updateError: null,
    updatingDraft: false,
    updateDraftError: null,
    employeeData: null,
    employeeDataLoading: false,
    employeeDataError: null,
};

const employerCertificateSlice = createSlice({
    name: 'employerCertificate',
    initialState,
    reducers: {
        resetCertificateData(state) {
            state.certificateData = [];
            state.error = null;
        },
        clearSelectedCertificate(state) {
            state.selectedCertificate = null;
            state.selectedCertificateError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCertificateData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getCertificateData.fulfilled, (state, action) => {
                state.loading = false;
                state.certificateData = action.payload.response;
            })
            .addCase(getCertificateData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? 'Something went wrong';
            })
            .addCase(submitCertificate.pending, (state) => {
                state.submitting = true;
                state.submitError = null;
            })
            .addCase(submitCertificate.fulfilled, (state) => {
                state.submitting = false;
            })
            .addCase(submitCertificate.rejected, (state, action) => {
                state.submitting = false;
                state.submitError = action.payload ?? 'Something went wrong';
            })
            .addCase(saveDraftCertificate.pending, (state) => {
                state.savingDraft = true;
                state.saveDraftError = null;
            })
            .addCase(saveDraftCertificate.fulfilled, (state) => {
                state.savingDraft = false;
            })
            .addCase(saveDraftCertificate.rejected, (state, action) => {
                state.savingDraft = false;
                state.saveDraftError = action.payload ?? 'Something went wrong';
            })
            .addCase(getEmployeeCertificates.pending, (state) => {
                state.certificatesLoading = true;
                state.certificatesError = null;
            })
            .addCase(getEmployeeCertificates.fulfilled, (state, action) => {
                state.certificatesLoading = false;
                state.employeeCertificates = action.payload.response;
            })
            .addCase(getEmployeeCertificates.rejected, (state, action) => {
                state.certificatesLoading = false;
                state.certificatesError = action.payload ?? 'Something went wrong';
            })
            .addCase(getDraftEmployeeCertificates.pending, (state) => {
                state.draftCertificatesLoading = true;
                state.draftCertificatesError = null;
            })
            .addCase(getDraftEmployeeCertificates.fulfilled, (state, action) => {
                state.draftCertificatesLoading = false;
                state.draftEmployeeCertificates = action.payload.response;
            })
            .addCase(getDraftEmployeeCertificates.rejected, (state, action) => {
                state.draftCertificatesLoading = false;
                state.draftCertificatesError = action.payload ?? 'Something went wrong';
            })
            .addCase(getCertificateById.pending, (state) => {
                state.selectedCertificateLoading = true;
                state.selectedCertificateError = null;
                state.selectedCertificate = null;
            })
            .addCase(getCertificateById.fulfilled, (state, action) => {
                state.selectedCertificateLoading = false;
                state.selectedCertificate = action.payload.response;
            })
            .addCase(getCertificateById.rejected, (state, action) => {
                state.selectedCertificateLoading = false;
                state.selectedCertificateError = action.payload ?? 'Something went wrong';
            })
            .addCase(getDraftCertificateById.pending, (state) => {
                state.selectedCertificateLoading = true;
                state.selectedCertificateError = null;
                state.selectedCertificate = null;
            })
            .addCase(getDraftCertificateById.fulfilled, (state, action) => {
                state.selectedCertificateLoading = false;
                state.selectedCertificate = action.payload.response;
            })
            .addCase(getDraftCertificateById.rejected, (state, action) => {
                state.selectedCertificateLoading = false;
                state.selectedCertificateError = action.payload ?? 'Something went wrong';
            })
            .addCase(updateCertificate.pending, (state) => {
                state.updating = true;
                state.updateError = null;
            })
            .addCase(updateCertificate.fulfilled, (state) => {
                state.updating = false;
            })
            .addCase(updateCertificate.rejected, (state, action) => {
                state.updating = false;
                state.updateError = action.payload ?? 'Something went wrong';
            })
            .addCase(updateDraftCertificate.pending, (state) => {
                state.updatingDraft = true;
                state.updateDraftError = null;
            })
            .addCase(updateDraftCertificate.fulfilled, (state) => {
                state.updatingDraft = false;
            })
            .addCase(updateDraftCertificate.rejected, (state, action) => {
                state.updatingDraft = false;
                state.updateDraftError = action.payload ?? 'Something went wrong';
            })
            .addCase(deleteCertificate.pending, (state) => {
                state.deleting = true;
                state.deleteError = null;
            })
            .addCase(deleteCertificate.fulfilled, (state, action) => {
                state.deleting = false;
                state.employeeCertificates = state.employeeCertificates.filter(
                    (c) => c.certificateId !== action.payload
                );
            })
            .addCase(deleteCertificate.rejected, (state, action) => {
                state.deleting = false;
                state.deleteError = action.payload ?? 'Something went wrong';
            })
            .addCase(deleteDraftCertificate.pending, (state) => {
                state.deleting = true;
                state.deleteError = null;
            })
            .addCase(deleteDraftCertificate.fulfilled, (state, action) => {
                state.deleting = false;
                state.draftEmployeeCertificates = state.draftEmployeeCertificates.filter(
                    (c) => c.certificateId !== action.payload
                );
            })
            .addCase(deleteDraftCertificate.rejected, (state, action) => {
                state.deleting = false;
                state.deleteError = action.payload ?? 'Something went wrong';
            })
            .addCase(getEmployeeById.pending, (state) => {
                state.employeeDataLoading = true;
                state.employeeDataError = null;
            })
            .addCase(getEmployeeById.fulfilled, (state, action) => {
                state.employeeDataLoading = false;
                state.employeeData = action.payload;
            })
            .addCase(getEmployeeById.rejected, (state, action) => {
                state.employeeDataLoading = false;
                state.employeeDataError = action.payload ?? 'Something went wrong';
            });
    },
});

export const { resetCertificateData, clearSelectedCertificate } = employerCertificateSlice.actions;
export default employerCertificateSlice.reducer;
